/**
 * Module-scope cache for the status chip's "Waiting on" tooltip.
 *
 * Kept free of imports so the auth slice can clear it on sign-out and company
 * switch without pulling the tooltip component (and the store) into a cycle.
 * Sign-out resets Redux without reloading the page, so anything held here would
 * otherwise outlive the session it was fetched in.
 */

const cache = new Map<string, Promise<unknown>>();

/**
 * The cached lookup for this key, or a new one from `load`. Keyed by the caller
 * on transaction and step, so a row that has moved on is asked again. A failed
 * lookup is dropped so the next open retries it.
 */
export const cachedWaitingOn = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const cached = cache.get(key) as Promise<T> | undefined;
  if (cached) return cached;
  const request = load().catch((err: unknown) => {
    cache.delete(key);
    throw err;
  });
  cache.set(key, request);
  return request;
};

export const clearWaitingOnCache = (): void => cache.clear();
